/**
 * Lightweight, typo-tolerant fuzzy search.
 *
 * The old search used `"jennifer waldman".includes(query)` with the WHOLE query
 * string, so typing "jen waldman" failed to match "Jennifer Waldman" (the
 * contiguous substring "jen waldman" isn't present). Per Reham's request, search
 * should behave more like Google: tolerate partial names, word order, and small
 * typos.
 *
 * Matching rules — the query is split into whitespace tokens, and EVERY token
 * must match the haystack via one of:
 *   1. substring anywhere in the haystack  ("jen" -> "jennifer")
 *   2. small Levenshtein edit distance to any word ("gaines" -> "haynes")
 * Longer tokens tolerate more typos; very short tokens require an exact substring
 * to avoid noise.
 */

/** Classic Levenshtein edit distance (two-row, O(a*b) time, O(b) space). */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let curr = new Array<number>(b.length + 1);

  for (let i = 0; i < a.length; i++) {
    curr[0] = i + 1;
    for (let j = 0; j < b.length; j++) {
      const cost = a[i] === b[j] ? 0 : 1;
      curr[j + 1] = Math.min(
        curr[j] + 1,        // insertion
        prev[j + 1] + 1,    // deletion
        prev[j] + cost      // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

/** How many typos to forgive for a token of a given length. */
function maxDistanceFor(token: string): number {
  if (token.length <= 3) return 0; // short tokens: substring only (avoid false hits)
  if (token.length <= 5) return 1;
  return 2;
}

function tokenMatchesWord(token: string, word: string): boolean {
  if (word.includes(token)) return true; // handles "jen" -> "jennifer"
  const max = maxDistanceFor(token);
  if (max === 0) return false;
  if (levenshtein(token, word) <= max) return true;
  // Compare against the same-length prefix so long words aren't penalised for length.
  if (word.length > token.length && levenshtein(token, word.slice(0, token.length)) <= max) {
    return true;
  }
  return false;
}

/**
 * Returns true if `query` fuzzily matches the combined `fields`.
 * Empty/whitespace query matches everything (no filtering).
 */
export function fuzzyMatch(query: string, ...fields: Array<string | null | undefined>): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const haystack = fields.filter(Boolean).join(' ').toLowerCase();
  if (!haystack) return false;
  if (haystack.includes(q)) return true; // fast path: exact contiguous match

  const words = haystack.split(/\s+/).filter(Boolean);
  const tokens = q.split(/\s+/).filter(Boolean);

  // Every query token must match somewhere in the haystack.
  return tokens.every(
    token => haystack.includes(token) || words.some(word => tokenMatchesWord(token, word))
  );
}
